<template>
    <div :ref="id" :id="id" class="h-100">
    </div>
</template>


<script>
    import {getFunctionGetWFString} from "@/services/data-tools.js";
    import * as d3 from "d3";
    import * as _ from 'lodash';

    export default {
        name: "PieChart",
        props: {
            id: String,
            color: String,
            data: Array,
            attrName: Object,
            innerRadius: {
                type: Number,
                default: 0
            },
            outerRadius: {
                type: Number,
                default: 200
            },
            labelWidth: {
                type: Number,
                default: 200
            },
            labelDistance: {
                type: Number,
                default: 200
            },
            longLabel: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                chart: {},
                svg: null,
                leftLabels: [],
                rightLabels: []
            };
        },
        mounted() {
            this.chart.data = this.data;
            this.renderPie();
        },
        methods: {
            renderPie() {
                var margin = {top: 10, right: 30, bottom: 20, left: 30},
                    width = this.$refs[this.id].clientWidth - margin.left - margin.right,
                    height = this.$refs[this.id].clientHeight - margin.top - margin.bottom;
                this.chart.radius = this.outerRadius;
                d3.select("#svg-" + this.id).remove();
                this.leftLabels = [];
                this.rightLabels = [];

                var gPie = d3
                    .select("#" + this.id)
                    .append("svg")
                    .attr("id", "svg-" + this.id)
                    .attr("width", width)
                    .attr("height", height)
                    .append("g")
                    .attr("transform", "translate(" + width / 2 + "," + height / 2 + ")");

                gPie.append("g").attr("class", "labels");
                this.chart.gPie = gPie;

                // this.createGradients();

                var pie = d3
                    .pie()
                    .sort(null)
                    .value(d => d[this.attrName.percent]);
                const data = this.data;

                var path = d3.arc()
                    .outerRadius(this.outerRadius - 1)
                    .innerRadius(this.innerRadius);

                var outerArc = d3.arc()
                    .innerRadius(this.innerRadius)
                    .outerRadius(this.outerRadius);

                this.chart.outerArc = outerArc;
                this.buildLabelArrays(pie(data));


                this.leftLabels = _.sortBy(this.leftLabels, "y");
                this.rightLabels = _.sortBy(this.rightLabels, "y");

                this.recalculateY(this.leftLabels);
                this.recalculateY(this.rightLabels, true);

                var arc = gPie.selectAll(".arc")
                    .data(pie(data))
                    .enter().append("g")
                    .style("stroke-width", "3px")
                    .attr("class", "arc");
                this.createGradients();
                arc.append("path")
                    .attr("d", path)
                    .attr("stroke", "white")
                    .style("stroke-width", "2px")
                    .attr("fill", () => {
                        return this.color;
                    })
                    .attr("fill", function (d, i) {
                        return "url(#grad" + (i % 3) + ")";
                    });
                const labels = _.concat(this.leftLabels, this.rightLabels);

                gPie.selectAll(".labels")
                    .data(labels, d => d)
                    .enter()
                    .append("foreignObject")
                    .attr("width", this.labelWidth)
                    .attr("height", 60)
                    .attr("transform", (d) => {
                        const pos = [d.x, d.y]
                        pos[0] = d.x < 0 ? d.x - this.labelWidth : d.x;
                        return "translate(" + pos + ")";
                    })
                    .append("xhtml:div")
                    .style("font", "12px 'Helvetica Neue'")
                    .html(d => {
                      let html = `<div class="chart-tooltip">${d.data.cropName}<br/>`;
                      if (d.data[this.attrName.value] > 0) {
                        html += `${(d.data[this.attrName.percent] * 100).toFixed(1)}%`;
                        if (this.longLabel) {
                          html += ` (${getFunctionGetWFString("yr")(d.data[this.attrName.value])})`;
                        }
                      }
                      html += "</div>";
                      return html;
                    });
                gPie.selectAll("polyline")
                    .data(labels, (d) => d)
                    .enter()
                    .append("polyline")
                    .attr("points", (d) => {
                        const posLabel = [d.x, d.y + 20];
                        const innerPoint = [d.pieCentral[0], d.y + 20];

                        return [posLabel, innerPoint, d.pieCentral];
                    })
                    .style("fill", "none")
                    .attr("stroke", 'black')
                    .style("stroke-width", "1px");

                gPie.selectAll("circle")
                    .data(labels, (d) => d)
                    .enter()
                    .append("circle")
                    .attr("cx", (d) => d.pieCentral[0])
                    .attr("cy", (d) => d.pieCentral[1])
                    .attr("r", 4)
                    .attr("fill", "white")
                    .attr("stroke-width", 2)
                    .attr("stroke", "black");
            },
            midAngle(d) {
                return d.startAngle + (d.endAngle - d.startAngle) / 2;
            },
            buildLabelArrays(data) {
                const maxLabelsNumber = data.length / 2;
                data.forEach(d => {
                    var pos = this.chart.outerArc.centroid(d);
                    var direction = pos[0] <= 0 ? -1 : 1;
                    d.pieCentral = pos;
                    d.y = d.y < 0 ? pos[1] - 50 : pos[1];
                    if (direction < 0 && this.leftLabels.length < maxLabelsNumber) {
                        this.leftLabels.push(d);
                        d.x = -this.labelDistance;
                    } else {
                        if (this.rightLabels.length > maxLabelsNumber) {
                            d.x = -this.labelDistance;
                            this.leftLabels.push(d);
                        } else {
                            d.x = this.labelDistance;
                            this.rightLabels.push(d);
                        }
                    }
                });
            },
            recalculateY(labels, isLeft = false) {
                const diff = isLeft ? 50 : 55;
                const minY = Math.round(labels.length / 2) * - diff;
                labels.forEach((d, i) => {
                    d.y = minY + i * 75;
                });
            },
            createGradients() {
                let colors = [];
                if (this.color === 'blue') {
                    colors = ['#039be5', '#0277bd', '#01579b'];
                }
                if (this.color === 'green') {
                    colors = ['#43a047', '#2e7d32', '#1b5e20'];
                }
                if (this.color === 'grey') {
                    colors = ['#546e7a', '#37474f', '#263238'];
                }
                if (this.color === 'total') {
                    colors = ['#e45a00', '#c24d00', '#ff8843'];
                }
                var grads = this.chart.gPie.append("defs").selectAll("radialGradient").data(colors)
                    .enter().append("radialGradient")
                    .attr("gradientUnits", "userSpaceOnUse")
                    .attr("cx", 0)
                    .attr("cy", 0)
                    .attr("r", "80%")
                    .attr("id", function (d, i) {
                        return "grad" + i;
                    });
                grads.append("stop").attr("offset", "0%").style("stop-color", function (d, i) {
                    return colors[i % 3]
                });
                grads.append("stop").attr("offset", "50%").style("stop-color", "white");
            }
        },
        watch: {
            "color": function () {
                this.renderPie();
            },
            "data": function (val) {
                this.chart.data = val;
                this.renderPie();
            },
            "innerRadius": function () {
                this.renderPie();
            }
        }
    };
</script>
